import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { formatFullDate, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

function remainingParts(deadline: Date): { days: number; hours: number; minutes: number } | null {
  const diff = deadline.getTime() - Date.now();
  if (diff <= 0) return null;
  const minutes = Math.floor(diff / 60000);
  return {
    days: Math.floor(minutes / (60 * 24)),
    hours: Math.floor((minutes % (60 * 24)) / 60),
    minutes: minutes % 60,
  };
}

export function DeadlineCountdown({ deadlineIso }: { deadlineIso: string }) {
  const { t } = useTranslation();
  const { numeralOptions, arabicNumerals } = useLocale();
  const deadline = new Date(deadlineIso);
  const [, setTick] = useState(0);

  useEffect(() => {
    const handle = setInterval(() => setTick((tick) => tick + 1), 30_000);
    return () => clearInterval(handle);
  }, []);

  const parts = remainingParts(deadline);

  const digits = (value: number) => localizeDigits(String(value), arabicNumerals);

  return (
    <View className="gap-1">
      <AppText variant="micro" weight="medium">
        {t('fantasy.gameweek.deadline')} · {formatFullDate(deadline, numeralOptions)}
      </AppText>
      {parts ? (
        <AppText variant="heading" weight="bold" tabular className="text-volt">
          {parts.days > 0
            ? t('fantasy.gameweek.countdown', {
                days: digits(parts.days),
                hours: digits(parts.hours),
                minutes: digits(parts.minutes),
              })
            : t('fantasy.gameweek.countdownShort', {
                hours: digits(parts.hours),
                minutes: digits(parts.minutes),
              })}
        </AppText>
      ) : (
        <AppText variant="caption" weight="semibold" className="text-loss">
          {t('fantasy.gameweek.deadlinePassed')}
        </AppText>
      )}
    </View>
  );
}
